/**
 * The apps an online appointment's meeting link can come from.
 *
 * One list, in one place: the Integrate apps card on My brand → Configuration
 * draws a row per entry, and the Schedule dialog shows the same marks in the
 * strip that asks for one to be connected. Two copies of "Google meet, Zoom"
 * would drift the moment a third app is added.
 */

import type { MeetingProviderSlug } from "./types/appointments.types";

export type MeetingAppId = "google-meet" | "zoom";

/**
 * How the backend names each of them in a path — its `slug`.
 *
 * Here beside the list rather than in the hook that reads the settings,
 * because two parts of the app need it for two different reasons: the hook
 * puts it in `/meeting-providers/{slug}/connect/` and matches it against the
 * `providers` the settings come back with, and the callback page puts it in
 * the call that finishes the handshake. Two copies would be two places to
 * forget the next app.
 *
 * `MeetingProviderSlug` is the wire's own type for these words — imported
 * rather than restated so a slug the backend does not know cannot be written
 * here.
 */
export const APP_SLUGS: Record<MeetingAppId, MeetingProviderSlug> = {
  "google-meet": "google",
  zoom: "zoom",
};

export interface MeetingApp {
  id: MeetingAppId;
  /** As the practice reads it — sentence case, the way the design writes it. */
  name: string;
  /** What connecting it buys, in one line. */
  description: string;
  /**
   * False while there is nothing to connect to yet: the row is still drawn,
   * with "Coming soon" where its button would be. Drawn rather than left out
   * because the card is also a statement of what is on the way, and an app
   * that appears from nowhere in a later release reads as one nobody was
   * told about.
   *
   * Zoom is false while its app is still in Zoom's review. Everything that
   * connects it is written and left in place — the slug, the hook, the
   * callback page — because none of it is reachable with no button to press,
   * and the day the review clears this is one word. Google Meet ships to
   * production meanwhile, which is the whole point of the flag: the two apps
   * do not have to go out together.
   */
  available: boolean;
}

export const MEETING_APPS: MeetingApp[] = [
  {
    id: "google-meet",
    name: "Google meet",
    description:
      "Connect your Google meet account with Naurist & create appointments automatically.",
    available: true,
  },
  {
    id: "zoom",
    name: "Zoom",
    description:
      "Connect your Zoom account with Naurist & create appointments automatically.",
    available: false,
  },
];
